<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { SmartPickerModal, type SmartPickerOption } from '@yuksalish/ui'
import { formatApiError } from '@yuksalish/api-client'
import { useHrApi } from '../composables/useHrApi'
import type { Option } from '../types/hr'

/**
 * Workplace picker for an employee's `department_agent_id`: departments,
 * internal teams, and warehouses loaded from the agents API (`GET /agents`).
 */
const props = withDefaults(
  defineProps<{
    open: boolean
    title?: string
    eyebrow?: string
    searchLabel?: string
    searchPlaceholder?: string
    emptyMessage?: string
    loadingMessage?: string
    /** Id of the workplace already set on the record, highlighted and brought into view. */
    selectedId?: number | null
  }>(),
  {
    title: '',
    eyebrow: '',
    searchLabel: '',
    searchPlaceholder: '',
    emptyMessage: '',
    loadingMessage: '',
    selectedId: null,
  },
)

const emit = defineEmits<{
  close: []
  select: [workplace: Option]
}>()

const { t } = useI18n()
const { listWorkplaceOptions } = useHrApi()

const ROLE_LABEL_KEYS: Record<string, string> = {
  warehouse: 'hr.picker.typeWarehouse',
  internal: 'hr.picker.typeInternal',
  department: 'hr.picker.typeDepartment',
}

const searchValue = ref('')
const departments = ref<Option[]>([])
const loading = ref(false)
const error = ref('')
const page = ref(1)

/** Role filter over the merged workplace list. */
const roleFilter = ref<string>('all')

const ROLE_FILTER_OPTIONS = ['all', 'department', 'internal', 'warehouse']

/** Rows shown per page; the merged workplace list is paginated client-side. */
const PAGE_SIZE = 20

/** `Department`, `Internal department`, or `Warehouse`, from the agent roles. */
function typeLabel(roles?: string[]): string {
  const role = (roles ?? []).find((value) => ROLE_LABEL_KEYS[value] !== undefined)
  if (!role) return ''
  const label = t(ROLE_LABEL_KEYS[role])
  return label && !String(label).startsWith('hr.') ? label : ''
}

const filtered = computed<SmartPickerOption[]>(() => {
  const search = searchValue.value.trim().toLowerCase()
  return departments.value
    .filter((department) => roleFilter.value === 'all' || (department.roles ?? []).includes(roleFilter.value))
    .filter((department) => !search || department.name.toLowerCase().includes(search))
    .map((department) => ({
      id: department.id,
      title: department.name,
      meta: typeLabel(department.roles),
    }))
})

const totalPages = computed(() => Math.max(1, Math.ceil(filtered.value.length / PAGE_SIZE)))

const paged = computed(() => {
  const start = (page.value - 1) * PAGE_SIZE
  return filtered.value.slice(start, start + PAGE_SIZE)
})

/** `Page 2 of 3 · 27 items` so the pager always shows what is on screen. */
const rangeLabel = computed(() =>
  t('hr.picker.pageRange', { page: page.value, total: totalPages.value, count: filtered.value.length }),
)

watch([searchValue, roleFilter], () => {
  page.value = 1
})

watch(totalPages, (value) => {
  if (page.value > value) page.value = value
})

function goToPage(next: number) {
  page.value = Math.min(Math.max(1, next), totalPages.value)
}

/** Opens the picker on the page that holds the already-selected workplace. */
function jumpToSelection() {
  const selected = props.selectedId
  if (selected === null || selected === undefined) return
  const index = filtered.value.findIndex((option) => String(option.id) === String(selected))
  if (index >= 0) page.value = Math.floor(index / PAGE_SIZE) + 1
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    departments.value = await listWorkplaceOptions()
    jumpToSelection()
  } catch (cause) {
    error.value = formatApiError(cause)
    departments.value = []
  } finally {
    loading.value = false
  }
}

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return
    searchValue.value = ''
    roleFilter.value = 'all'
    page.value = 1
    void load()
  },
)

onMounted(() => {
  if (props.open) void load()
})
</script>

<template>
  <SmartPickerModal
    :open="open"
    :title="title || t('hr.picker.departmentTitle')"
    :eyebrow="eyebrow || t('hr.picker.departmentEyebrow')"
    :search-label="searchLabel || t('hr.picker.departmentSearch')"
    :search-placeholder="searchPlaceholder || t('hr.picker.departmentPlaceholder')"
    :search-value="searchValue"
    :loading="loading"
    :error-message="error"
    :empty-message="emptyMessage || t('hr.picker.departmentEmpty')"
    :items="paged"
    :page="page"
    :total-pages="totalPages"
    :selected-id="selectedId"
    :page-label="rangeLabel"
    :close-aria-label="t('hr.picker.close')"
    :retry-label="t('hr.picker.retry')"
    :previous-label="t('hr.picker.previous')"
    :next-label="t('hr.picker.next')"
    :cancel-label="t('hr.picker.cancel')"
    @close="emit('close')"
    @retry="load"
    @previous="goToPage(page - 1)"
    @next="goToPage(page + 1)"
    @select="(option: SmartPickerOption) => {
      const match = departments.find((department) => String(department.id) === String(option.id))
      if (match) emit('select', match)
    }"
    @update:search-value="(value: string) => (searchValue = value)"
  >
    <template #helper>
      <label class="picker-type">
        <span>{{ t('hr.picker.typeFilter') }}</span>
        <select v-model="roleFilter">
          <option v-for="option in ROLE_FILTER_OPTIONS" :key="option" :value="option">
            {{ option === 'all' ? t('hr.picker.typeAll') : typeLabel([option]) }}
          </option>
        </select>
      </label>
    </template>
    <template #loading>{{ loadingMessage || t('hr.picker.departmentLoading') }}</template>
  </SmartPickerModal>
</template>

<style scoped>
.picker-type {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #53625d;
  font-size: 13px;
  font-weight: 700;
}
.picker-type select {
  padding: 6px 10px;
  border: 1px solid #aebdcc;
  border-radius: 6px;
  background: #fff;
  font: inherit;
  font-size: 13px;
}
</style>